import { Box, Typography } from '@mui/material';

export interface TimeseriesPoint {
  bucket: string;
  requests: number;
}

interface Props {
  points: TimeseriesPoint[];
  height?: number;
  color?: string;
}

/**
 * Tiny SVG line chart — no chart-library dependency. Renders one series of
 * counts indexed by ISO bucket. The X axis is evenly spaced (no time gaps);
 * good enough for the dashboard's "trend at a glance" use case.
 */
export default function TimeseriesChart({ points, height = 160, color = '#1a3353' }: Props) {
  if (!points.length) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: 'text.secondary',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2">No data yet</Typography>
      </Box>
    );
  }

  const W = 800;
  const H = height;
  const PAD = 32;
  const max = Math.max(1, ...points.map((p) => p.requests));
  const stepX = points.length === 1 ? 0 : (W - PAD * 2) / (points.length - 1);

  const pts = points.map((p, i) => {
    const x = PAD + i * stepX;
    const y = H - PAD - (p.requests / max) * (H - PAD * 2);
    return { x, y, p };
  });
  const path = pts.map((pt, i) => `${i === 0 ? 'M' : 'L'} ${pt.x} ${pt.y}`).join(' ');
  const fillPath = `${path} L ${pts[pts.length - 1].x} ${H - PAD} L ${pts[0].x} ${H - PAD} Z`;

  const yTicks = [0, Math.round(max / 2), max];
  const xTicks = pts
    .filter((_, i) => {
      const stride = Math.max(1, Math.ceil(pts.length / 6));
      return i % stride === 0;
    })
    .slice(0, 6);

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
        {/* Y gridlines */}
        {yTicks.map((tv) => {
          const y = H - PAD - (tv / max) * (H - PAD * 2);
          return (
            <g key={tv}>
              <line x1={PAD} x2={W - PAD} y1={y} y2={y} stroke="#e2e8f0" strokeDasharray="2 4" />
              <text x={PAD - 4} y={y + 4} fontSize={10} fill="#64748b" textAnchor="end">
                {tv}
              </text>
            </g>
          );
        })}
        <path d={fillPath} fill={color} fillOpacity={0.08} />
        <path d={path} fill="none" stroke={color} strokeWidth={2} />
        {pts.map((pt, i) => (
          <circle key={i} cx={pt.x} cy={pt.y} r={2.5} fill={color}>
            <title>{`${formatBucket(pt.p.bucket)}: ${pt.p.requests}`}</title>
          </circle>
        ))}
        {xTicks.map((pt, i) => (
          <text
            key={i}
            x={pt.x}
            y={H - PAD + 14}
            fontSize={10}
            fill="#64748b"
            textAnchor="middle"
          >
            {formatBucket(pt.p.bucket)}
          </text>
        ))}
      </svg>
    </Box>
  );
}

function formatBucket(b: string): string {
  const d = new Date(b);
  if (Number.isNaN(d.getTime())) return b;
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}
